import type { InputBaseComponentProps } from '@mui/material';

import React from 'react';
import { NumericFormat } from 'react-number-format';

export type CurrencyInputConfig = {
  /** Number of decimal places allowed (0 for whole numbers, 2 for cents) */
  decimalScale: number;
  /** Whether to always show decimal places (e.g., "100" becomes "100.00") */
  fixedDecimalScale: boolean;
  /** Thousand separator: true for comma, false for none, or custom string (default: true) */
  thousandSeparator?: boolean | string;
};

export type CurrencyInputProps = Omit<InputBaseComponentProps, 'defaultValue'> & {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
  defaultValue?: string | number;
};

/**
 * Factory function to create currency input components with configurable formatting.
 */
export const createCurrencyInput = (config: CurrencyInputConfig) => {
  const { decimalScale, fixedDecimalScale, thousandSeparator = true } = config;

  return React.forwardRef<HTMLInputElement, CurrencyInputProps>(function CurrencyInput(props, ref) {
    const { onChange, ...other } = props;

    return (
      <NumericFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: values.value,
            },
          });
        }}
        thousandSeparator={thousandSeparator}
        decimalScale={decimalScale}
        fixedDecimalScale={fixedDecimalScale}
        valueIsNumericString={true}
      />
    );
  });
};

/**
 * Currency input for whole numbers only (no decimals allowed).
 * Use when decimals should be blocked at input level.
 */
export const CurrencyWholeInput = createCurrencyInput({
  decimalScale: 0,
  fixedDecimalScale: false,
});
CurrencyWholeInput.displayName = 'CurrencyWholeInput';

/**
 * Currency input allowing up to 2 decimal places (no auto-append).
 * Use for fields like Gross Monthly Rent where decimals are validated by Yup.
 */
export const CurrencyDecimalInput = createCurrencyInput({
  decimalScale: 2,
  fixedDecimalScale: false,
});
CurrencyDecimalInput.displayName = 'CurrencyDecimalInput';

/**
 * Currency input with fixed 2 decimal places (auto-appends ".00").
 * Use for fields like Prepaid Rent, Free Rent where decimal formatting is expected.
 */
export const CurrencyFixedDecimalInput = createCurrencyInput({
  decimalScale: 2,
  fixedDecimalScale: true,
});
CurrencyFixedDecimalInput.displayName = 'CurrencyFixedDecimalInput';
